import type { Reframe } from '../types'

export type FormatKey = 'original' | 'r16x9' | 'r9x16' | 'r1x1' | 'r4x5'

export interface Format {
  key: FormatKey
  label: string
  short: string
  /** kích thước canvas đích; 0 = theo clip gốc */
  w: number
  h: number
}

export const FORMATS: Format[] = [
  { key: 'original', label: 'Gốc (giữ nguyên tỉ lệ)', short: 'Gốc', w: 0, h: 0 },
  { key: 'r16x9', label: '16:9 Ngang — YouTube', short: '16:9', w: 1920, h: 1080 },
  { key: 'r9x16', label: '9:16 Dọc — TikTok / Reels / Shorts', short: '9:16', w: 1080, h: 1920 },
  { key: 'r1x1', label: '1:1 Vuông — Feed', short: '1:1', w: 1080, h: 1080 },
  { key: 'r4x5', label: '4:5 Dọc — Instagram Feed', short: '4:5', w: 1080, h: 1350 },
]

export const getFormat = (k: FormatKey): Format => FORMATS.find((f) => f.key === k)!

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))

/** Kích thước canvas cho một format (Gốc = theo clip cơ sở). */
export function canvasSize(fmt: Format, base: { width: number; height: number }) {
  if (fmt.key === 'original') return { w: base.width || 1280, h: base.height || 720 }
  return { w: fmt.w, h: fmt.h }
}

/**
 * Vùng vẽ clip lên canvas:
 * - Gốc → "contain" (letterbox, không cắt).
 * - Tỉ lệ khác → "cover" (lấp đầy, cắt phần thừa) canh theo điểm lấy nét.
 */
export function computeDraw(
  clip: { width: number; height: number },
  cw: number,
  ch: number,
  fmt: Format,
  rf: Reframe,
): Rect {
  const sw = clip.width || cw
  const sh = clip.height || ch
  if (fmt.key === 'original') {
    const s = Math.min(cw / sw, ch / sh)
    const w = sw * s
    const h = sh * s
    return { x: (cw - w) / 2, y: (ch - h) / 2, w, h }
  }
  const s = Math.max(cw / sw, ch / sh)
  const dw = sw * s
  const dh = sh * s
  const fx = rf.mode === 'center' ? 0.5 : rf.fx
  const fy = rf.mode === 'center' ? 0.5 : rf.fy
  const x = clamp(cw * 0.5 - fx * dw, cw - dw, 0)
  const y = clamp(ch * 0.5 - fy * dh, ch - dh, 0)
  return { x, y, w: dw, h: dh }
}

/**
 * Khung crop biểu diễn theo tỉ lệ 0..1 của video hiển thị — dùng vẽ guide overlay
 * trên preview để người dùng thấy phần sẽ được giữ khi đổi tỉ lệ.
 */
export function guideFraction(
  sw: number,
  sh: number,
  fmt: Format,
  rf: Reframe,
): { left: number; top: number; w: number; h: number } | null {
  if (fmt.key === 'original') return null
  const A = fmt.w / fmt.h
  const srcA = sw / sh
  let fw: number
  let fh: number
  if (A < srcA) {
    fh = 1
    fw = (sh * A) / sw
  } else {
    fw = 1
    fh = sw / A / sh
  }
  const fx = rf.mode === 'center' ? 0.5 : rf.fx
  const fy = rf.mode === 'center' ? 0.5 : rf.fy
  const left = clamp(fx - fw / 2, 0, 1 - fw)
  const top = clamp(fy - fh / 2, 0, 1 - fh)
  return { left, top, w: fw, h: fh }
}
